import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Braces, Calendar, Copy, Eye, Loader2, RefreshCw, RotateCcw, Send, Webhook, X, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PanelTableCard,
  PanelTableHeader,
  PanelTableFrame,
  PanelTableHeadRow,
  PanelTableHead,
  PanelTableBody,
  PanelTableRow,
  PanelTableCell,
  PanelTableEmpty,
  PanelTableActions,
} from "@/components/ui/panel-table";

const STATUS_FILTERS = [
  { id: "all", label: "Tümü" },
  { id: "1", label: "Başarılı" },
  { id: "0", label: "Hatalı" },
];

const WebhookPushLogsSettings = ({ apiBase, isDark, textClass, labelClass, companies = [], isSuperAdmin = false, userCompanyId = null }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState(isSuperAdmin ? "all" : String(userCompanyId || ""));
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLog, setDetailLog] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsExpandedAgent, setStatsExpandedAgent] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [pushing, setPushing] = useState(false);

  const selectedCompanyId = useMemo(() => {
    if (isSuperAdmin) {
      if (!companyFilter || companyFilter === "all") return null;
      return Number(companyFilter);
    }
    return userCompanyId ? Number(userCompanyId) : null;
  }, [isSuperAdmin, companyFilter, userCompanyId]);

  const inputClass = isDark
    ? "bg-slate-950/80 border-slate-700 text-white placeholder:text-slate-500"
    : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400";
  const cardClass = isDark ? "bg-slate-950/50 border-slate-800" : "bg-white border-gray-200 shadow-sm";
  const pillClass = (active) => (
    active
      ? "bg-slate-700 text-white border-slate-600"
      : isDark
        ? "border-slate-700 text-slate-300 hover:bg-slate-800"
        : "border-gray-200 text-gray-600 hover:bg-gray-50"
  );
  const dateInputClass = `[&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;
  const monoAreaClass = isDark
    ? "bg-slate-950 border-slate-700 text-cyan-200 font-mono text-xs"
    : "bg-gray-50 border-gray-200 text-cyan-900 font-mono text-xs";

  const copyText = async (text, label = "Metin") => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopyalandı`);
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (isSuperAdmin && companyFilter && companyFilter !== "all") params.company_id = companyFilter;
      if (statusFilter !== "all") params.success = statusFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${apiBase}/webhook-push-logs`, { params });
      setLogs(res.data?.logs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Webhook logları yüklenemedi");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, companyFilter, statusFilter, startDate, endDate, isSuperAdmin]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const fetchStats = useCallback(async () => {
    if (!selectedCompanyId) {
      setStats(null);
      return;
    }
    setStatsLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${apiBase}/companies/${selectedCompanyId}/webhook-status`, { params });
      setStats(res.data?.stats || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Webhook durumu yüklenemedi");
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [apiBase, selectedCompanyId, startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const resetDeliveries = async () => {
    if (!selectedCompanyId) return;
    const pending = stats?.pending_calls ?? 0;
    const delivered = stats?.delivered_calls ?? 0;
    const ok = window.confirm(
      `Bu firmanın webhook gönderim durumu sıfırlanacak.\n\n` +
      `Şu an: ${delivered} gönderildi, ${pending} bekliyor.\n\n` +
      `Sıfırlama sonrası tüm kayıtlar yeniden 12'li paketler halinde gönderilir. Devam?`
    );
    if (!ok) return;
    setResetting(true);
    try {
      const res = await axios.post(`${apiBase}/companies/${selectedCompanyId}/webhook-status`, {
        reset_deliveries: true,
      });
      setStats(res.data?.stats || null);
      toast.success(res.data?.message || "Gönderim durumu sıfırlandı");
      await fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sıfırlama başarısız");
    } finally {
      setResetting(false);
    }
  };

  const pushWebhookBatch = async (pushAll = false) => {
    if (!selectedCompanyId) return;
    setPushing(true);
    try {
      const body = {
        push_all: pushAll,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      const res = await axios.post(`${apiBase}/companies/${selectedCompanyId}/webhook-settings`, body);
      const result = res.data?.result || {};
      if (result.success) {
        if (pushAll) {
          toast.success(`${result.batch_count || 0} paket · ${result.total_calls_sent || 0} çağrı gönderildi · kalan ${result.remaining_pending ?? 0}`);
        } else {
          toast.success(`Paket gönderildi: ${result.call_count || 0} çağrı · kalan ${result.pending_after ?? 0}`);
        }
      } else if ((result.call_count ?? result.total_calls_sent) === 0) {
        toast.info(result.message || "Gönderilecek kayıt yok");
      } else {
        toast.error(result.error || res.data?.message || "Gönderim başarısız");
      }
      await Promise.all([fetchStats(), fetchLogs()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.result?.error || "Gönderim başarısız");
      await fetchStats();
    } finally {
      setPushing(false);
    }
  };

  const openDetail = async (log) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailLog(log);
    try {
      const res = await axios.get(`${apiBase}/webhook-push-logs/${log.id}`);
      setDetailLog(res.data?.log || log);
    } catch {
      setDetailLog(log);
    } finally {
      setDetailLoading(false);
    }
  };

  const detailPayloadText = useMemo(() => {
    if (!detailLog?.payload_preview) return "";
    try {
      return JSON.stringify(JSON.parse(detailLog.payload_preview), null, 2);
    } catch {
      return detailLog.payload_preview;
    }
  }, [detailLog]);

  return (
    <>
      <PanelTableCard isDark={isDark} className={cardClass}>
        <PanelTableHeader
          isDark={isDark}
          textClass={textClass}
          labelClass={labelClass}
          accent="emerald"
          icon={Webhook}
          title="Webhook Push Logları"
          description="Firma bazlı cron webhook gönderim geçmişi ve POST edilen veri önizlemesi"
          toolbar={(
            <Button type="button" variant="outline" size="sm" onClick={fetchLogs} disabled={loading} className={isDark ? "border-slate-600" : ""}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          )}
        />

        <div className={`mx-4 mb-3 p-3 rounded-lg border ${isDark ? "border-slate-700/80 bg-slate-900/40" : "border-gray-200 bg-gray-50/80"}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
            {isSuperAdmin && (
              <div className="lg:col-span-3">
                <label className={`text-xs font-medium ${labelClass} block mb-1.5`}>Firma</label>
                <Select value={companyFilter || "all"} onValueChange={setCompanyFilter}>
                  <SelectTrigger className={`h-9 text-xs w-full ${inputClass}`}><SelectValue placeholder="Tüm firmalar" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="all">Tüm firmalar</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={isSuperAdmin ? "lg:col-span-2" : "lg:col-span-3"}>
              <label className={`text-xs font-medium ${labelClass} block mb-1.5`}>Log başlangıç</label>
              <div className="relative">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`h-9 w-full text-xs pl-9 pr-8 ${inputClass} ${dateInputClass}`}
                />
                <Calendar className={`w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                {startDate && (
                  <button
                    type="button"
                    onClick={() => setStartDate("")}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded ${isDark ? "text-slate-500 hover:text-slate-300" : "text-gray-400 hover:text-gray-600"}`}
                    aria-label="Başlangıç tarihini temizle"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className={isSuperAdmin ? "lg:col-span-2" : "lg:col-span-3"}>
              <label className={`text-xs font-medium ${labelClass} block mb-1.5`}>Log bitiş</label>
              <div className="relative">
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={`h-9 w-full text-xs pl-9 pr-8 ${inputClass} ${dateInputClass}`}
                />
                <Calendar className={`w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                {endDate && (
                  <button
                    type="button"
                    onClick={() => setEndDate("")}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded ${isDark ? "text-slate-500 hover:text-slate-300" : "text-gray-400 hover:text-gray-600"}`}
                    aria-label="Bitiş tarihini temizle"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className={`${isSuperAdmin ? "lg:col-span-5" : "lg:col-span-6"} flex flex-wrap items-center gap-1.5 min-h-9`}>
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setStatusFilter(f.id)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs border transition-colors ${pillClass(statusFilter === f.id)}`}
                >
                  {f.label}
                </button>
              ))}
              {(startDate || endDate) && (
                <Button type="button" variant="ghost" size="sm" onClick={clearDateFilters} className={`h-8 text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  Tarihi sıfırla
                </Button>
              )}
            </div>
          </div>
        </div>

        {selectedCompanyId && (
          <div className={`mx-4 mb-3 p-3 rounded-lg border ${isDark ? "border-emerald-900/50 bg-emerald-950/20" : "border-emerald-200 bg-emerald-50/60"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <p className={`text-sm font-medium ${textClass}`}>Gönderim durumu</p>
                <p className={`text-[11px] ${labelClass}`}>
                  Her POST en fazla {stats?.batch_size || 12} çağrı · başarılı olanlar bir daha gönderilmez
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={fetchStats} disabled={statsLoading || resetting || pushing} className={isDark ? "border-slate-600" : ""}>
                  {statsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => pushWebhookBatch(false)} disabled={statsLoading || resetting || pushing || !(stats?.pending_calls > 0)} className={isDark ? "border-slate-600" : ""}>
                  {pushing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  1 paket gönder
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => pushWebhookBatch(true)} disabled={statsLoading || resetting || pushing || !(stats?.pending_calls > 0)} className={isDark ? "border-slate-600" : ""}>
                  {pushing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Bitene kadar gönder
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={resetDeliveries} disabled={statsLoading || resetting || pushing} className={isDark ? "border-rose-800 text-rose-300 hover:bg-rose-950/40" : "border-rose-300 text-rose-700 hover:bg-rose-50"}>
                  {resetting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                  Sıfırla
                </Button>
              </div>
            </div>

            {statsLoading && !stats ? (
              <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
            ) : stats ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: "Toplam", value: stats.total_calls, color: isDark ? "text-slate-200" : "text-gray-800" },
                    { label: "Gönderildi", value: stats.delivered_calls, color: isDark ? "text-emerald-300" : "text-emerald-700" },
                    { label: "Bekleyen", value: stats.pending_calls, color: isDark ? "text-amber-300" : "text-amber-700" },
                    { label: "İlerleme", value: `%${stats.progress_percent ?? 0}`, color: isDark ? "text-cyan-300" : "text-cyan-700" },
                  ].map((item) => (
                    <div key={item.label} className={`rounded-lg border px-3 py-2 ${isDark ? "border-slate-700/80 bg-slate-900/50" : "border-gray-200 bg-white"}`}>
                      <p className={`text-[10px] uppercase tracking-wide ${labelClass}`}>{item.label}</p>
                      <p className={`text-lg font-semibold tabular-nums ${item.color}`}>{item.value}</p>
                    </div>
                  ))}
                </div>

                {stats.agents?.length > 0 && (
                  <PanelTableFrame isDark={isDark}>
                    <PanelTableHeadRow isDark={isDark}>
                      <PanelTableHead>Agent</PanelTableHead>
                      <PanelTableHead className="text-right">Toplam</PanelTableHead>
                      <PanelTableHead className="text-right">Gönderildi</PanelTableHead>
                      <PanelTableHead className="text-right">Bekleyen</PanelTableHead>
                      <PanelTableHead className="text-right">%</PanelTableHead>
                      <PanelTableHead className="text-right">Liste</PanelTableHead>
                    </PanelTableHeadRow>
                    <PanelTableBody>
                      {stats.agents.map((agent) => (
                        <React.Fragment key={agent.id}>
                          <PanelTableRow isDark={isDark}>
                            <PanelTableCell>
                              <div className="text-xs">
                                <p className={textClass}>{agent.ad_soyad || agent.kullanici_adi}</p>
                                <p className={labelClass}>{agent.kullanici_adi}</p>
                              </div>
                            </PanelTableCell>
                            <PanelTableCell className="text-right tabular-nums">{agent.total_calls}</PanelTableCell>
                            <PanelTableCell className={`text-right tabular-nums ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>{agent.delivered_calls}</PanelTableCell>
                            <PanelTableCell className={`text-right tabular-nums ${isDark ? "text-amber-300" : "text-amber-700"}`}>{agent.pending_calls}</PanelTableCell>
                            <PanelTableCell className="text-right tabular-nums">{agent.progress_percent}%</PanelTableCell>
                            <PanelTableCell className="text-right">
                              {agent.pending_calls > 0 && (
                                <Button type="button" variant="ghost" size="icon" onClick={() => setStatsExpandedAgent(statsExpandedAgent === agent.id ? null : agent.id)} title="Bekleyen kayıtlar">
                                  {statsExpandedAgent === agent.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </Button>
                              )}
                            </PanelTableCell>
                          </PanelTableRow>
                          {statsExpandedAgent === agent.id && (
                            <PanelTableRow isDark={isDark}>
                              <PanelTableCell colSpan={6}>
                                <div className={`text-[11px] rounded-lg border p-2 ${isDark ? "border-slate-700 bg-slate-950/60" : "border-gray-200 bg-gray-50"}`}>
                                  {(stats.next_pending || []).find((g) => g.agent_id === agent.id)?.calls?.length ? (
                                    <div className="overflow-x-auto">
                                      <table className="w-full">
                                        <thead>
                                          <tr className={labelClass}>
                                            <th className="text-left py-1 pr-2">ID</th>
                                            <th className="text-left py-1 pr-2">Tarih</th>
                                            <th className="text-left py-1 pr-2">Yön</th>
                                            <th className="text-left py-1 pr-2">Durum</th>
                                            <th className="text-left py-1">Numara</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(stats.next_pending.find((g) => g.agent_id === agent.id)?.calls || []).map((call) => (
                                            <tr key={call.id} className={isDark ? "border-t border-slate-800" : "border-t border-gray-100"}>
                                              <td className="py-1 pr-2 tabular-nums">{call.id}</td>
                                              <td className="py-1 pr-2 whitespace-nowrap">{call.tarih}</td>
                                              <td className="py-1 pr-2">{call.yon}</td>
                                              <td className="py-1 pr-2">{call.durum}</td>
                                              <td className="py-1">{call.arayan || call.aranan || "-"}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <p className={labelClass}>Sıradaki pakette bu agent için kayıt yok (daha eski kayıtlar başka agentlarda olabilir).</p>
                                  )}
                                </div>
                              </PanelTableCell>
                            </PanelTableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </PanelTableBody>
                  </PanelTableFrame>
                )}

                {stats.pending_calls > 0 && stats.next_pending?.length > 0 && (
                  <p className={`text-[10px] ${labelClass}`}>
                    Sıradaki paket: {stats.next_pending_total} kayıt gösteriliyor (max {stats.batch_size || 12} gönderilir)
                  </p>
                )}
              </div>
            ) : (
              <p className={`text-xs ${labelClass}`}>Durum bilgisi alınamadı.</p>
            )}
          </div>
        )}

        {isSuperAdmin && (!companyFilter || companyFilter === "all") && (
          <p className={`mx-4 mb-3 text-xs ${labelClass}`}>Agent gönderim analizi için bir firma seçin.</p>
        )}

        <PanelTableFrame isDark={isDark}>
          <PanelTableHeadRow isDark={isDark}>
            <PanelTableHead>Tarih</PanelTableHead>
            {isSuperAdmin && <PanelTableHead>Firma</PanelTableHead>}
            <PanelTableHead>Durum</PanelTableHead>
            <PanelTableHead>Çağrı aralığı</PanelTableHead>
            <PanelTableHead className="text-right">Agent</PanelTableHead>
            <PanelTableHead className="text-right">Çağrı</PanelTableHead>
            <PanelTableHead className="text-right">HTTP</PanelTableHead>
            <PanelTableHead className="text-right">ms</PanelTableHead>
            <PanelTableHead className="text-right">Detay</PanelTableHead>
          </PanelTableHeadRow>
          <PanelTableBody>
            {loading ? (
              <PanelTableEmpty colSpan={isSuperAdmin ? 9 : 8} labelClass={labelClass}>
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-emerald-500" />
              </PanelTableEmpty>
            ) : logs.length === 0 ? (
              <PanelTableEmpty colSpan={isSuperAdmin ? 9 : 8} labelClass={labelClass}>
                Henüz webhook log kaydı yok
              </PanelTableEmpty>
            ) : (
              logs.map((log) => (
                <PanelTableRow key={log.id} isDark={isDark}>
                  <PanelTableCell className="whitespace-nowrap text-xs">{log.created_at || "-"}</PanelTableCell>
                  {isSuperAdmin && <PanelTableCell className="text-xs">{log.company_name || log.company_id}</PanelTableCell>}
                  <PanelTableCell>
                    <Badge className={log.success ? "bg-emerald-600/20 text-emerald-400" : "bg-rose-600/20 text-rose-400"}>
                      {log.success ? "OK" : "Hata"}
                    </Badge>
                  </PanelTableCell>
                  <PanelTableCell className="text-xs whitespace-nowrap">
                    {(log.filter_start_date || log.filter_end_date)
                      ? `${log.filter_start_date || "…"} → ${log.filter_end_date || "…"}`
                      : "Tümü"}
                  </PanelTableCell>
                  <PanelTableCell className="text-right tabular-nums">{log.agent_count}</PanelTableCell>
                  <PanelTableCell className="text-right tabular-nums">{log.call_count}</PanelTableCell>
                  <PanelTableCell className="text-right tabular-nums">{log.http_status ?? "-"}</PanelTableCell>
                  <PanelTableCell className="text-right tabular-nums">{log.duration_ms ?? "-"}</PanelTableCell>
                  <PanelTableCell className="text-right">
                    <PanelTableActions>
                      <Button type="button" variant="ghost" size="icon" onClick={() => openDetail(log)} title="Detay / JSON">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </PanelTableActions>
                  </PanelTableCell>
                </PanelTableRow>
              ))
            )}
          </PanelTableBody>
        </PanelTableFrame>
      </PanelTableCard>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className={`max-w-3xl max-h-[85vh] overflow-y-auto ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Braces className="w-5 h-5 text-emerald-500" />
              Webhook Log Detayı #{detailLog?.id || ""}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
          ) : detailLog ? (
            <div className="space-y-3 text-sm">
              <div className={`grid grid-cols-2 gap-2 text-xs ${labelClass}`}>
                <div>Firma: <span className={textClass}>{detailLog.company_name || detailLog.company_id}</span></div>
                <div>Batch: <span className="font-mono">{detailLog.batch_id}</span></div>
                <div>URL: <span className="break-all">{detailLog.webhook_url}</span></div>
                <div>Metot: {detailLog.http_method}</div>
                <div>Çağrı aralığı: {(detailLog.filter_start_date || detailLog.filter_end_date) ? `${detailLog.filter_start_date || "…"} → ${detailLog.filter_end_date || "…"}` : "Tümü"}</div>
                <div>HTTP: {detailLog.http_status ?? "-"} · {detailLog.duration_ms ?? "-"} ms</div>
              </div>
              {!detailLog.success && detailLog.error_message && (
                <p className={`text-xs ${isDark ? "text-rose-300" : "text-rose-600"}`}>{detailLog.error_message}</p>
              )}
              {detailLog.response_preview && (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <p className={`text-xs font-medium ${labelClass}`}>Uzak sunucu yanıtı</p>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => copyText(detailLog.response_preview, "Yanıt")}>
                      <Copy className="w-3.5 h-3.5 mr-1" /> Kopyala
                    </Button>
                  </div>
                  <Textarea
                    readOnly
                    value={detailLog.response_preview}
                    rows={4}
                    className={`min-h-[96px] resize-y ${monoAreaClass}`}
                  />
                </div>
              )}
              {detailPayloadText ? (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <p className={`text-xs font-medium ${labelClass}`}>Gönderilen JSON (payload)</p>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => copyText(detailPayloadText, "JSON")}>
                      <Copy className="w-3.5 h-3.5 mr-1" /> Kopyala
                    </Button>
                  </div>
                  <Textarea
                    readOnly
                    value={detailPayloadText}
                    rows={16}
                    className={`min-h-[280px] max-h-[50vh] resize-y ${monoAreaClass}`}
                  />
                </div>
              ) : (
                <p className={`text-xs ${labelClass}`}>Bu log için kaydedilmiş payload önizlemesi yok (eski kayıt olabilir).</p>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WebhookPushLogsSettings;
